import { Tooltip } from "antd";
import { SwapOutlined } from "@ant-design/icons";

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

/**
 * Currency Utilities for the Accounting Module
 * 
 * This file provides currency conversion and formatting utilities.
 * All monetary values in the database are stored in USD.
 * Conversion happens only at display time based on global settings.
 * 
 * NOTE: The centralized utilities are in src/utils/currency-utils.js
 * This file maintains backward compatibility with existing imports.
 */

// Re-export from centralized utilities for new code
export {
  formatMoney,
  getDisplayCurrency,
  getDisplaySymbol,
  getCurrentRate,
  isConversionEnabled,
  getMoneyTooltipInfo,
  currencyValueGetter,
  currencyValueFormatter,
} from "../../../utils/currency-utils";

// --- Currency Symbols Map (Global) ---
// Moved to top to be available for all functions
const GLOBAL_CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$',
  CHF: 'CHF', CNY: '¥', INR: '₹', MXN: 'MX$', BRL: 'R$', KRW: '₩',
  SGD: 'S$', HKD: 'HK$', NOK: 'kr', SEK: 'kr', DKK: 'kr', NZD: 'NZ$',
  ZAR: 'R', RUB: '₽', TRY: '₺', PLN: 'zł', THB: '฿', IDR: 'Rp',
  MYR: 'RM', PHP: '₱', AED: 'د.إ', SAR: '﷼', ILS: '₪', EGP: 'E£',
};

// --- Helper Functions ---

/**
 * Helper to normalize currency symbols and codes to their ISO 4217 format.
 * @param {string} currency The currency symbol or code (e.g., '$', 'EUR').
 * @returns {string} The normalized ISO code (e.g., 'USD').
 */
export const normalizeCurrency = (currency) => {
    if (!currency) return 'USD';
    const currencyMap = {
        '$': 'USD',
        '€': 'EUR',
        '£': 'GBP',
        '¥': 'JPY',
    };
    const normalized = String(currency).trim().toUpperCase();
    return currencyMap[normalized] || normalized;
};


// --- Main Conversion Functions ---

/**
 * Converts an amount from a given currency to USD.
 * ONLY uses custom rates from localStorage. If no rate is found, it returns the original amount.
 * @param {number} amount The amount to convert.
 * @param {string} currency The currency to convert from (e.g., 'EUR', '€').
 * @returns {number} The converted amount in USD, or the original amount if no custom rate is set.
 */
export const convertToUSD = (amount, currency, customRatesOverride) => {
  if (amount === null || amount === undefined || !currency) return amount;
  const normalizedCurrency = normalizeCurrency(currency);
  if (normalizedCurrency === "USD") return parseFloat(amount);

  try {
    // Prefer explicit rates passed in (from CreateInvoice/CreateCredit modals)
    let customRates = customRatesOverride;

    // If no override or it's an empty object, fall back to localStorage
    if (!customRates || Object.keys(customRates).length === 0) {
      // Support both legacy and new storage keys
      const snake = localStorage.getItem("custom_currency_rates");
      const camel = localStorage.getItem("customCurrencyRates");
      customRates = {};
      if (snake) {
        Object.assign(customRates, JSON.parse(snake));
      }
      if (camel) {
        Object.assign(customRates, JSON.parse(camel));
      }
    }

    if (customRates && customRates[normalizedCurrency]) {
      const rate = parseFloat(customRates[normalizedCurrency]);
      if (!rate || rate <= 0) {
        return parseFloat(amount);
      }
      // Stored rate is "1 USD = rate <currency>", so convert by dividing
      return parseFloat(amount) / rate;
    }
  } catch (e) {
    console.error(
      "Failed to read custom currency rates from localStorage",
      e
    );
  }

  // If no custom rate is found, return the original amount without conversion.
  return parseFloat(amount);
};

/**
 * Converts an amount from USD to a target currency.
 * ONLY uses custom rates from localStorage. If no rate is found, it returns the original amount.
 * @param {number} amount The amount in USD to convert.
 * @param {string} targetCurrency The currency to convert to (e.g., 'EUR', '€').
 * @returns {number} The converted amount in the target currency, or the original amount if no custom rate is set.
 */
export const convertFromUSD = (amount, targetCurrency, customRatesOverride) => {
  if (amount === null || amount === undefined || !targetCurrency)
    return amount;
  const normalizedCurrency = normalizeCurrency(targetCurrency);
  if (normalizedCurrency === "USD") return parseFloat(amount);

  let rate = null;

  try {
    // Prefer explicit rates passed in
    let customRates = customRatesOverride;

    // If no override or it's an empty object, fall back to localStorage
    if (!customRates || Object.keys(customRates).length === 0) {
      const snake = localStorage.getItem("custom_currency_rates");
      const camel = localStorage.getItem("customCurrencyRates");
      customRates = {};
      if (snake) {
        Object.assign(customRates, JSON.parse(snake));
      }
      if (camel) {
        Object.assign(customRates, JSON.parse(camel));
      }
    }

    if (customRates && customRates[normalizedCurrency]) {
      rate = parseFloat(customRates[normalizedCurrency]);
    }
  } catch (e) {
    console.error(
      "Failed to read custom currency rates from localStorage",
      e
    );
  }

  if (!rate || rate <= 0) return parseFloat(amount);
  // Stored rate is "1 USD = rate <currency>", so from USD we multiply
  return parseFloat(amount) * rate;
};

const formatWithCommas = (value) => {
  const dp = getDecimalPrecision();
  return parseFloat(value).toLocaleString('en-US', { 
    minimumFractionDigits: dp, 
    maximumFractionDigits: dp 
  });
};

// Format currency with USD conversion and exchange icon
// UPDATED: Now uses GLOBAL display currency for all amounts
// Shows swap icon whenever there's a currency difference (original vs display)
export const formatCurrencyWithConversion = (
  amount,
  originalCurrency,
  showOriginal = true
) => {
  if (amount == null || amount === undefined) return `$0.${"0".repeat(getDecimalPrecision())}`;
  
  // Value from backend is ALWAYS in USD
  const usdValue = parseFloat(amount) || 0;
  
  // Normalize original currency
  const normalizedOriginal = normalizeCurrency(originalCurrency) || 'USD';
  
  // Check for global display currency
  const globalSettings = getGlobalDisplayCurrency();
  const { currency: displayCurrency, symbol: displaySymbol, rate } = globalSettings;
  
  // CASE 1: Global display is NOT USD - convert and show swap icon
  if (displayCurrency !== 'USD' && rate && rate > 0) {
    const convertedValue = usdValue * rate;
    
    if (!showOriginal) {
      return `${displaySymbol} ${formatWithCommas(convertedValue)}`;
    }
    
    return (
      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span>{displaySymbol} {formatWithCommas(convertedValue)}</span>
        <Tooltip
          title={`Original: $${formatWithCommas(usdValue)} (Rate: 1 USD = ${rate} ${displayCurrency})`}
        >
          <SwapOutlined style={{ color: "#1890ff", fontSize: "12px" }} />
        </Tooltip>
      </span>
    );
  }

  // CASE 2: Global display is USD, but original currency was NOT USD
  // This means the value was converted to USD from another currency at entry time
  if (displayCurrency === 'USD' && normalizedOriginal !== 'USD') {
    const originalSymbol = GLOBAL_CURRENCY_SYMBOLS[normalizedOriginal] || normalizedOriginal;
    
    if (!showOriginal) {
      return `$${formatWithCommas(usdValue)}`;
    }
    
    return (
      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span>${formatWithCommas(usdValue)}</span>
        <Tooltip
          title={`Converted from ${originalSymbol} (${normalizedOriginal})`}
        >
          <SwapOutlined style={{ color: "#1890ff", fontSize: "12px" }} />
        </Tooltip>
      </span>
    );
  }

  // Default: show as USD (no conversion involved)
  return `$${formatWithCommas(usdValue)}`;
};

// Format currency for InputNumber formatter (shows original currency)
export const formatCurrencyForInput = (value, currency) => {
  if (!value && value !== 0) return "";
  // Convert to string to handle both number and string inputs (stringMode compatibility)
  const strValue = String(value);
  // Split by decimal point to format integer part only
  const parts = strValue.split('.');
  const integerPart = parts[0] || '0';
  const decimalPart = parts[1] ? `.${parts[1]}` : '';
  // Format integer part with commas, preserve decimal part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${currency} ${formattedInteger}${decimalPart}`;
};

// Parse currency from InputNumber (removes currency symbol and commas, preserves decimal point)
export const parseCurrencyFromInput = (value, currency) => {
  if (!value && value !== "") return "";
  // Convert to string to handle both number and string inputs
  const strValue = String(value);
  // Remove everything except digits, decimal points, and hyphens (for negative numbers)
  // This ensures decimal points are always preserved
  let parsed = strValue.replace(/[^\d.-]/g, "");
  // Allow decimal point even if it's the only character (user is typing ".")
  // This is critical for allowing direct decimal input
  if (parsed === "." || parsed === "-." || parsed === "-") {
    return parsed;
  }
  // Ensure only one decimal point
  const firstDotIndex = parsed.indexOf('.');
  if (firstDotIndex !== -1) {
    const beforeDot = parsed.substring(0, firstDotIndex);
    const afterDot = parsed.substring(firstDotIndex + 1).replace(/\./g, '');
    parsed = beforeDot + '.' + afterDot;
  }
  return parsed;
};

// Legacy no-op used by older code paths that expected to "preload" rates.
// We now rely entirely on localStorage / custom rates, so this just returns a resolved Promise.
export const preloadCurrencyRate = async () => {
  return;
};

export const formatCurrencyPlain = (value, symbol) => {
  const dp = getDecimalPrecision();
  if (value == null || value === undefined) return `${symbol} 0.${"0".repeat(dp)}`;
  const valueNum = parseFloat(value) || 0;
  return `${symbol} ${valueNum.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
};

// --- Global Display Currency Functions ---

/**
 * Get the current global display currency settings
 * @returns {{ currency: string, symbol: string, rate: number | null }}
 */
export const getGlobalDisplayCurrency = () => {
  // Default fallback rates for common currencies
  const DEFAULT_RATES = { EUR: 0.92, GBP: 0.79, JPY: 149.5, CAD: 1.36, AUD: 1.53 };
  
  try {
    let currency = localStorage.getItem('global_display_currency') || 'USD';
    
    // Handle legacy symbol values (convert symbols to codes)
    const symbolToCode = { '$': 'USD', '€': 'EUR', '£': 'GBP', '¥': 'JPY' };
    if (symbolToCode[currency]) {
      currency = symbolToCode[currency];
    }
    
    const symbol = GLOBAL_CURRENCY_SYMBOLS[currency] || currency;
    
    if (currency === 'USD') {
      return { currency, symbol, rate: 1 };
    }
    
    const ratesStr = localStorage.getItem('custom_currency_rates');
    const rates = ratesStr ? JSON.parse(ratesStr) : {};
    // Use stored rate, or fallback to default rate
    const rate = rates[currency] || DEFAULT_RATES[currency] || null;
    
    return { currency, symbol, rate };
  } catch (e) {
    return { currency: 'USD', symbol: '$', rate: 1 };
  }
};

/**
 * Convert USD amount to display currency
 * @param {number} usdAmount - Amount in USD
 * @returns {number} - Amount in display currency
 */
export const convertToDisplayCurrency = (usdAmount) => {
  if (usdAmount == null || usdAmount === undefined) return 0;
  const numAmount = parseFloat(usdAmount) || 0;
  
  const { currency, rate } = getGlobalDisplayCurrency();
  if (currency === 'USD' || !rate || rate <= 0) return numAmount;
  
  return numAmount * rate;
};

/**
 * Format amount in global display currency
 * @param {number} usdAmount - Amount in USD (database value)
 * @param {object} options - Formatting options
 * @returns {string} - Formatted currency string
 */
export const formatInDisplayCurrency = (usdAmount, options = {}) => {
  const { decimals = getDecimalPrecision(), showSymbol = true } = options;
  
  if (usdAmount == null || usdAmount === undefined) {
    const { symbol } = getGlobalDisplayCurrency();
    return showSymbol ? `${symbol} 0.${'0'.repeat(decimals)}` : `0.${'0'.repeat(decimals)}`;
  }
  
  const { symbol, rate, currency } = getGlobalDisplayCurrency();
  let value = parseFloat(usdAmount) || 0;
  
  // Convert if not USD and rate is set
  if (currency !== 'USD' && rate && rate > 0) {
    value = value * rate;
  }
  
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  return showSymbol ? `${symbol} ${formatted}` : formatted;
};

/**
 * Get display currency symbol
 * @returns {string} - Currency symbol
 */
export const getDisplayCurrencySymbol = () => {
  const { symbol } = getGlobalDisplayCurrency();
  return symbol;
};

/**
 * Check if global currency conversion is enabled
 * @returns {boolean}
 */
export const isGlobalCurrencyEnabled = () => {
  const { currency, rate } = getGlobalDisplayCurrency();
  return currency !== 'USD' && rate && rate > 0;
};

